import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Eraser, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { LOGO_URL } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  {
    heading: "1. PROJECT SCOPE",
    body: [
      "The project scope, coating system, finish, price, and other project-specific details are listed in the Crownvera Epoxy quote provided to the customer.",
      "The accepted quote and this agreement together make up the agreement between Crownvera Epoxy LLC (“Crownvera”) and the customer.",
      "Work not included in the original quote will be considered additional work.",
    ],
  },
  {
    heading: "2. PAYMENT TERMS",
    body: [
      "For projects totaling $25,000 or less, a 50% down payment is required before work begins. The remaining 50% is due immediately upon completion of the work described in the accepted quote.",
      "For applicable residential projects totaling more than $25,000, an initial down payment of 10% is required before work begins. Once Crownvera begins the contracted work, an additional progress payment will be due so that total payments equal 50% of the original contract price. The remaining 50% is due immediately upon completion of the contracted work.",
      "Final payment is due upon completion and is not subject to a waiting period. Minor touch-ups, warranty requests, or other items that do not prevent the contracted coating system from being completed do not delay payment of the final balance.",
      "Any approved additional work, repairs, materials, or services will be added to the final invoice unless otherwise agreed upon in writing.",
    ],
  },
  {
    heading: "3. CONCRETE PREPARATION & HIDDEN CONDITIONS",
    body: [
      "Proper concrete preparation is necessary for a successful coating installation. Crownvera mechanically prepares the concrete before applying the coating system. However, the complete condition of the concrete cannot always be seen before the surface is ground.",
      "Grinding may reveal previously hidden conditions including: cracks, pitting, spalling, weak or deteriorated concrete, damaged areas, previous repairs, adhesive or coating residue, oil or chemical contamination, uneven areas, and other hidden substrate conditions. These conditions may not be visible when the original quote is prepared and therefore may not be included in the original project price.",
      "If additional repairs are necessary before the coating can properly be installed, Crownvera will notify the customer and provide the cost of the additional work. The customer must approve the additional work before Crownvera performs the repair. Once approved, the additional cost will be added to the project invoice unless otherwise agreed upon.",
      "Crownvera will not knowingly install a coating over a condition that may compromise the finished coating system.",
    ],
  },
  {
    heading: "4. CRACKS & CONCRETE MOVEMENT",
    body: [
      "Existing cracks will be repaired when crack repair is included in the project scope or approved as additional work. Concrete naturally expands, contracts, settles, and moves.",
      "Crownvera cannot guarantee that an existing crack will never return or that a new crack will never develop after installation. Future cracks caused by movement of the concrete, settlement, structural movement, freeze/thaw conditions, control joints, or expansion joints are not considered a failure of Crownvera's coating installation.",
    ],
  },
  {
    heading: "5. MOISTURE",
    body: [
      "Crownvera tests the moisture condition of the concrete before coating when appropriate for the coating system being installed. The moisture test reflects the condition of the concrete at the time of testing.",
      "Moisture conditions can change after installation because of groundwater, hydrostatic pressure, drainage problems, plumbing leaks, foundation issues, flooding, weather, water intrusion, or other changes to the property. Crownvera is not responsible for coating failure caused by future moisture conditions that develop or materially change after installation.",
      "If Crownvera identifies a moisture issue before installation, additional treatment or moisture mitigation may be recommended before the coating is installed.",
    ],
  },
  {
    heading: "6. FINISHED APPEARANCE",
    body: [
      "Epoxy, metallic, flake, quartz, polyaspartic, and other decorative coating systems are applied by hand. Minor variations in color, pattern, metallic movement, flake distribution, texture, or sheen are normal and are not considered defects.",
      "Samples, photographs, website images, and digital or AI floor previews represent the general appearance of a finish. Because every concrete floor and installation is different, an exact duplicate cannot be guaranteed.",
    ],
  },
  {
    heading: "7. CURING & PROTECTING THE FLOOR",
    body: [
      "The coating must be allowed adequate time to cure before normal use. Unless Crownvera provides different written instructions for the specific coating system installed, the following general cure times apply:",
      "Light foot traffic: approximately 12–24 hours after completion. Normal/heavier foot traffic and placement of furniture or light equipment: approximately 24–48 hours after completion. Vehicle traffic: wait a minimum of 72 hours after completion. Heavy equipment, aggressive cleaning, pressure washing, or exposure to strong chemicals: wait approximately 5–7 days after completion.",
      "These time periods may need to be extended because of temperature, humidity, ventilation, weather, coating type, or other jobsite conditions. If Crownvera provides project-specific curing instructions, those instructions take priority over the general time periods listed above.",
      "The customer is responsible for keeping people, pets, vehicles, furniture, equipment, and unnecessary traffic away from the floor during the applicable curing period. The customer should not place mats, rugs, cardboard, plastic, rubber-backed coverings, or other materials over the newly installed coating until Crownvera advises that the floor has cured sufficiently.",
      "Damage caused by walking, driving, placing objects on, washing, chemically exposing, covering, or otherwise using the floor before the recommended cure time is not covered under warranty.",
      "Crownvera takes reasonable precautions during installation but cannot completely control the surrounding environment. Crownvera is not responsible for cosmetic imperfections or damage caused during curing by insects, birds, rodents, pets, wildlife, leaves, pollen, wind-blown debris, airborne contamination, or other conditions outside Crownvera's reasonable control.",
    ],
  },
  {
    heading: "8. CUSTOMER RESPONSIBILITIES",
    body: [
      "Unless otherwise stated in the quote, the customer agrees to: provide reasonable access to the project area; remove vehicles, furniture, personal property, inventory, and other obstacles; keep children, pets, employees, customers, and unauthorized individuals away from the work area; inform Crownvera of known moisture, drainage, chemical, or concrete problems; inform Crownvera of radiant floor heating or known utilities located in or beneath the concrete; and follow Crownvera's curing and maintenance instructions.",
      "Commercial customers are also responsible for coordinating access with employees, tenants, building management, customers, and other contractors unless otherwise agreed.",
    ],
  },
  {
    heading: "9. 10-YEAR LIMITED COATING WARRANTY",
    body: [
      "Unless a different warranty period is specifically stated in the project quote, Crownvera provides a 10-Year Limited Coating Warranty.",
      "Crownvera warrants that the coating system installed by Crownvera will not peel, flake, or delaminate from properly prepared concrete as a direct result of defective Crownvera surface preparation or installation workmanship during the warranty period.",
      "If Crownvera determines that a covered warranty failure has occurred, Crownvera will repair or recoat the affected area at no charge to the customer. The warranty applies to the affected area only and does not require replacement of unaffected portions of the floor. A repaired area may not perfectly match the surrounding floor because of normal aging, wear, color variation, or exposure.",
      "The warranty does not cover damage or failure caused by: new or recurring concrete cracks; concrete or structural movement; settlement; failure or deterioration of the underlying concrete; future moisture or hydrostatic pressure; flooding or water intrusion; plumbing or drainage problems; impact damage; gouges, cuts, burns, or scratches; dragging heavy or sharp objects; improper cleaning or maintenance; improper chemical exposure; damage caused by another contractor or third party; premature use before the coating has properly cured; normal wear; normal cosmetic scratching; normal loss of gloss; UV-related fading or discoloration unless otherwise specified; insects, animals, pets, wildlife, or airborne contamination during curing; abuse or misuse; or conditions outside Crownvera's reasonable control.",
    ],
  },
  {
    heading: "10. ADDITIONAL & UNFORESEEN WORK",
    body: [
      "During preparation, Crownvera may discover conditions that could not reasonably have been identified before work began. If this occurs: Crownvera will inform the customer of the condition, explain what needs to be repaired or corrected, and provide the additional cost. The customer must approve the additional work before it is performed. Once approved, the additional cost will be added to the project invoice unless another arrangement is agreed upon.",
      "If the condition must be repaired before the coating can properly be installed, Crownvera may pause installation until the repair is approved or another solution is agreed upon.",
    ],
  },
  {
    heading: "11. PROJECT SCHEDULING",
    body: [
      "Crownvera will make reasonable efforts to complete the project according to the anticipated schedule. Installation may occasionally be delayed because of weather, temperature, humidity, moisture conditions, material availability, unexpected concrete conditions, cure times, customer-requested changes, access issues, or other conditions outside Crownvera's reasonable control. Crownvera will communicate significant scheduling changes with the customer.",
    ],
  },
  {
    heading: "12. CANCELLATION",
    body: [
      "Any cancellation rights that apply to the project will be provided to the customer when required. Once any applicable cancellation period has passed and materials have been purchased, work has begun, or project-specific expenses have been incurred, the customer may be responsible for those costs if the project is canceled.",
    ],
  },
  {
    heading: "13. COMPLETE AGREEMENT",
    body: [
      "This agreement, the accepted Crownvera Epoxy quote, and any approved changes make up the agreement between Crownvera and the customer. Any changes to the project scope, price, payment schedule, or warranty must be approved by Crownvera and the customer. Electronic signatures and electronic approvals may be used.",
    ],
  },
];

function SignaturePad({ onInk, testid }) {
  const ref = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    const c = ref.current;
    const ratio = 2;
    c.width = c.offsetWidth * ratio;
    c.height = c.offsetHeight * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1C1917";
  }, []);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };

  const down = (e) => {
    drawing.current = true;
    const ctx = ref.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(...pos(e));
    ref.current.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const ctx = ref.current.getContext("2d");
    ctx.lineTo(...pos(e));
    ctx.stroke();
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onInk(ref.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = ref.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    onInk(null);
  };

  return (
    <div>
      <canvas
        ref={ref}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerLeave={up}
        className="w-full h-36 bg-white border border-amber-600/30 rounded-md cursor-crosshair touch-none"
        data-testid={testid}
      />
      <button type="button" onClick={clear} data-testid="signature-clear" className="mt-2 text-xs text-stone-500 hover:text-amber-700 flex items-center gap-1.5">
        <Eraser className="w-3.5 h-3.5" /> Clear signature
      </button>
    </div>
  );
}

export default function AgreementView() {
  const { id } = useParams();
  const [quote, setQuote] = useState(undefined);
  const [form, setForm] = useState({ name: "", business: "", billing_address: "", project_address: "", phone: "", email: "" });
  const [sigData, setSigData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${API}/quotes/${id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((q) => {
        setQuote(q);
        if (q) {
          setForm({
            name: q.customer?.name || "",
            business: q.customer?.business || "",
            billing_address: q.customer?.billing_address || "",
            project_address: q.customer?.project_address || "",
            phone: q.customer?.phone || "",
            email: q.customer?.email || "",
          });
        }
      })
      .catch(() => setQuote(null));
  }, [id]);

  if (quote === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]"><Loader2 className="w-6 h-6 text-amber-700 animate-spin" /></div>;
  }
  if (!quote) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] px-4 text-center"><h1 className="font-display text-2xl text-stone-900">Quote not found</h1></div>;
  }

  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Please type your full name.");
    if (!sigData) return toast.error("Please draw your signature.");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/quotes/${id}/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, business: form.business, data_url: sigData }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Could not save signature. Please try again.");
      }
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] py-10 px-4" data-testid="agreement-page">
      <Seo title={`Project Agreement ${quote.number} | Crownvera Epoxy`} description="Crownvera Epoxy Project Agreement & Limited Warranty." noindex />
      <div className="max-w-3xl mx-auto">
        <div className="gold-ring-card rounded-xl overflow-hidden">
          <div className="bg-stone-950 px-6 sm:px-10 py-8 text-center">
            <img src={LOGO_URL} alt="Crownvera Epoxy" className="h-16 w-auto mx-auto mb-4" />
            <h1 className="font-display text-2xl sm:text-3xl text-white tracking-wide">PROJECT AGREEMENT &amp; LIMITED WARRANTY</h1>
            <p className="text-amber-400 text-sm font-semibold mt-2">Crownvera Epoxy LLC · Quote {quote.number}</p>
          </div>

          <div className="p-6 sm:p-10 text-sm text-stone-700 leading-relaxed space-y-6">
            {done || quote.signed ? (
              <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-5" data-testid="agreement-signed-banner">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-900">Agreement signed &amp; accepted</p>
                  <p className="text-emerald-800 mt-1">
                    {(quote.signed?.name || form.name)} · {quote.signed ? new Date(quote.signed.date).toLocaleDateString() : today}. We have everything we need — Crownvera will be in touch to schedule your project.
                  </p>
                  <Link to={`/quote/${id}`} data-testid="agreement-back-to-quote" className="inline-block mt-3 text-xs font-bold uppercase tracking-wider text-amber-700 hover:text-amber-800">
                    ← Back to quote {quote.number}
                  </Link>
                </div>
              </div>
            ) : null}

            <p className="text-stone-600">
              Thank you for choosing Crownvera Epoxy LLC. This agreement works together with quote {quote.number} provided for your project and outlines the project terms, payment requirements, responsibilities, and warranty.
            </p>

            <section className="bg-[#F5F2EB] rounded-lg p-5 sm:p-6" data-testid="agreement-customer-info">
              <h2 className="font-display text-lg text-amber-900 mb-4">CUSTOMER INFORMATION</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block"><span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Customer Name *</span>
                  <input className="input-dark mt-1" value={form.name} onChange={set("name")} data-testid="agreement-name" /></label>
                <label className="block"><span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Business Name, if applicable</span>
                  <input className="input-dark mt-1" value={form.business} onChange={set("business")} data-testid="agreement-business" /></label>
                <label className="block"><span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Billing Address</span>
                  <input className="input-dark mt-1" value={form.billing_address} onChange={set("billing_address")} data-testid="agreement-billing" /></label>
                <label className="block"><span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Project Address</span>
                  <input className="input-dark mt-1" value={form.project_address} onChange={set("project_address")} data-testid="agreement-project-address" /></label>
                <label className="block"><span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Phone</span>
                  <input className="input-dark mt-1" value={form.phone} onChange={set("phone")} data-testid="agreement-phone" /></label>
                <label className="block"><span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Email</span>
                  <input className="input-dark mt-1" type="email" value={form.email} onChange={set("email")} data-testid="agreement-email" /></label>
              </div>
              <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-xs text-stone-600">
                <span>Quote / Proposal #: <strong className="text-stone-900">{quote.number}</strong></span>
                <span>Project Type: <strong className="text-stone-900 capitalize">{quote.project_type}</strong></span>
                <span>Quote Total: <strong className="text-stone-900">${Number(quote.total).toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong></span>
              </div>
            </section>

            {SECTIONS.map((s) => (
              <section key={s.heading}>
                <h2 className="font-display text-lg text-amber-900 mb-2">{s.heading}</h2>
                {s.body.map((p, i) => (
                  <p key={i} className="mb-2">{p}</p>
                ))}
              </section>
            ))}

            <section className="border-t-2 border-amber-600/30 pt-6">
              <h2 className="font-display text-lg text-amber-900 mb-3">CUSTOMER ACCEPTANCE</h2>
              <p className="mb-2">I have reviewed and accepted the Crownvera Epoxy quote and this Project Agreement &amp; Limited Warranty.</p>
              <p className="mb-2">I understand the project scope, payment schedule, requirement that the final balance is due upon completion, concrete preparation process, possibility of hidden concrete conditions, additional-work procedure, moisture limitations, curing requirements, and warranty terms.</p>
              <p className="mb-5">I authorize Crownvera Epoxy LLC to perform the work described in the accepted quote.</p>

              <div className="bg-[#F5F2EB] rounded-lg p-5 sm:p-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="block"><span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Customer / Authorized Representative *</span>
                    <input className="input-dark mt-1" value={form.name} onChange={set("name")} data-testid="acceptance-name" /></label>
                  <label className="block"><span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Date</span>
                    <input className="input-dark mt-1 bg-stone-100" value={today} readOnly data-testid="acceptance-date" /></label>
                </div>
                <div>
                  <span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Signature * (draw with finger or mouse)</span>
                  <div className="mt-1"><SignaturePad onInk={setSigData} testid="signature-pad" /></div>
                </div>
              </div>

              <div className="mt-6 bg-stone-950 rounded-lg p-5 sm:p-6 text-stone-300" data-testid="crownvera-acceptance">
                <h3 className="font-display text-base text-amber-400 mb-4">CROWNVERA EPOXY LLC</h3>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-stone-500">Authorized Representative</p>
                    <p className="mt-1 font-semibold text-white">Roman Pashkovskiy, Owner</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-stone-500">Date</p>
                    <p className="mt-1 font-semibold text-white">{today}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-xs uppercase tracking-wider text-stone-500">Signature</p>
                  <p className="font-display italic text-3xl text-amber-300 mt-1" style={{ fontFamily: "'Playfair Display', serif" }}>Roman Pashkovskiy</p>
                </div>
              </div>

              {!done && !quote.signed && (
                <button
                  onClick={submit}
                  disabled={submitting}
                  data-testid="agreement-sign-btn"
                  className="mt-6 w-full bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider text-sm py-4 rounded-md shadow-md transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {submitting ? "Signing..." : "Sign & Accept Agreement"}
                </button>
              )}
            </section>
          </div>
        </div>
        <p className="text-center text-xs text-stone-400 mt-6">Crownvera Epoxy LLC · 440-381-7828 · crownveraepoxy.com</p>
      </div>
    </div>
  );
}
